// @ts-nocheck
// The choreography model + deterministic forward simulator.
//
// This is the source of truth for BOTH the live chart preview and (later) the
// on-chain executor. It walks an ordered timeline of steps, applies every action
// to a pump.fun bonding curve using the SAME validated engine the real launch
// uses (pumpSimulationMath.ts), and threads a per-wallet inventory ledger through
// the whole run — so "sell 25%" leaves "75% remaining" on that exact wallet.
//
// Accuracy note: trades are timestamped >=1s apart so each becomes its own candle
// (bit-exact per the sim's validation); a "bundle" step shares one timestamp (one
// slot), collapsing into a single candle just like a real Jito bundle.

import {
  createInitialPumpState,
  executePumpTradeIntent,
  lamportsToSol,
  marketCapSolFromState,
  pumpBondingProgressFromState,
  rawToTokenUi,
  candlesFromMarketCapPrints,
  type PumpBondingState,
  type SimPrint,
  type SimCandle,
} from "@/lib/algo/pumpSimulationMath";
import type { SimChartMarker } from "@/components/algo/SimChartPanel";

export const PROTOCOL_FEE_BPS = 95;
export const CREATOR_FEE_BPS = 30;
export const DEFAULT_GAP_MS = 1000;

export type SendMode = "bundle" | "normal";

export type Action =
  | { id: string; kind: "buy"; walletId: string; sol: number }
  | { id: string; kind: "sell"; walletId: string; pct: number }
  | { id: string; kind: "rotate"; walletId: string; toWalletId: string; sellPct: number }
  | { id: string; kind: "airdrop"; walletId: string; toListId: string; sellPct: number };

export type Step = {
  id: string;
  label: string;
  sendMode: SendMode;
  /** ms advanced per trade inside the step (normal) / after the step (bundle). */
  gapMs: number;
  actions: Action[];
};

export type Wallet = {
  id: string;
  label: string;
  group: string;
  fundingSol: number;
  loopIntervalMs?: number;
};

export type Choreography = {
  name: string;
  devBuySol: number;
  solUsd: number;
  bucketMs: number;
  wallets: Wallet[];
  steps: Step[];
  /** Per-wallet price-target take-profits: sell pct% when mcap hits x× entry. */
  sellTargets?: { walletId: string; x: number; pct: number }[];
  /** Timed/immediate sells fired after the buy phase at atMs (now = 0). */
  timedSells?: { walletId: string; atMs: number; pct: number }[];
  /** The live tape: every trade with an ABSOLUTE timestamp (ms), in time order.
   *  Built from the playground (graph) — candles are derived strictly from this. */
  tape?: TapeAction[];
};

export type TapeAction = {
  t: number; // absolute ms from launch
  kind: "buy" | "sell" | "rotate" | "airdrop";
  walletId: string;
  toWalletId?: string;
  sol?: number;
  pct?: number;
  sellPct?: number;
  label: string;    // primary trader name (buyer for buy; seller for sell/rotate-source)
  toTrader?: string; // rotate: the receiving wallet's name (for its buy-back print)
};

export type WalletLedger = {
  walletId: string;
  cashSol: number;
  tokens: number;
  boughtTokens: number;
  costBasisSol: number;
  realizedSol: number;
  buys: number;
  sells: number;
};

export type SimResult = {
  prints: SimPrint[];
  candles: SimCandle[];
  markers: SimChartMarker[];
  ledgers: WalletLedger[];
  finalState: PumpBondingState;
  marketCapSol: number;
  marketCapUsd: number;
  bondingProgress: number;
  totalSolIn: number;
  totalSolOut: number;
  errors: string[];
};

let idCounter = 0;
export function uid(prefix = "id"): string {
  idCounter += 1;
  return `${prefix}-${idCounter}-${Math.floor(performance.now())}`;
}

function emptyLedger(wallet: Wallet): WalletLedger {
  return {
    walletId: wallet.id,
    cashSol: wallet.fundingSol,
    tokens: 0,
    boughtTokens: 0,
    costBasisSol: 0,
    realizedSol: 0,
    buys: 0,
    sells: 0,
  };
}

/** Remaining holdings as a % of everything this wallet has ever bought. */
export function remainingPct(ledger: WalletLedger): number {
  if (ledger.boughtTokens <= 0) return 0;
  return Math.max(0, Math.min(100, (ledger.tokens / ledger.boughtTokens) * 100));
}

type Ctx = {
  state: PumpBondingState;
  ledgers: Map<string, WalletLedger>;
  prints: SimPrint[];
  markers: SimChartMarker[];
  errors: string[];
  solUsd: number;
  totalIn: number;
  totalOut: number;
};

function recordPrint(
  ctx: Ctx,
  ts: number,
  side: "buy" | "sell",
  walletSol: number,
  curveSol: number,
  tokens: number,
  label: string,
) {
  const mcSol = marketCapSolFromState(ctx.state);
  ctx.prints.push({
    ts,
    side,
    walletSol,
    sol: curveSol,
    tokenAmount: tokens,
    marketCapSol: mcSol,
    marketCapUsd: mcSol * ctx.solUsd,
    state: ctx.state,
    label,
  });
  ctx.markers.push({ id: uid("mk"), time: ts, side, label, priceSol: mcSol });
}

function doBuy(ctx: Ctx, walletId: string, sol: number, ts: number, label: string) {
  const ledger = ctx.ledgers.get(walletId);
  if (!ledger) return;
  if (!(sol > 0)) return;
  const exec = executePumpTradeIntent(ctx.state, {
    side: "buy",
    sol,
    buySolMode: "wallet-gross",
    protocolFeeBps: PROTOCOL_FEE_BPS,
    creatorFeeBps: CREATOR_FEE_BPS,
  });
  const walletSol = lamportsToSol(exec.walletSolLamports);
  const curveSol = lamportsToSol(exec.curveSolLamports);
  const tokens = rawToTokenUi(exec.tokenRaw);
  if (tokens <= 0) {
    ctx.errors.push(`${label}: buy produced no tokens (curve may be complete)`);
    return;
  }
  if (walletSol > ledger.cashSol + 1e-9) {
    ctx.errors.push(`${label}: ${shortId(walletId)} short ${(walletSol - ledger.cashSol).toFixed(4)} SOL`);
  }
  ctx.state = exec.next;
  ledger.cashSol -= walletSol;
  ledger.tokens += tokens;
  ledger.boughtTokens += tokens;
  ledger.costBasisSol += walletSol;
  ledger.buys += 1;
  ctx.totalIn += walletSol;
  recordPrint(ctx, ts, "buy", walletSol, curveSol, tokens, label);
  return { walletSol, tokens };
}

function doSell(ctx: Ctx, walletId: string, pct: number, ts: number, label: string) {
  const ledger = ctx.ledgers.get(walletId);
  if (!ledger) return;
  const tokensToSell = ledger.tokens * (Math.max(0, Math.min(100, pct)) / 100);
  if (!(tokensToSell > 0)) {
    ctx.errors.push(`${label}: ${shortId(walletId)} has nothing to sell`);
    return;
  }
  const exec = executePumpTradeIntent(ctx.state, {
    side: "sell",
    tokenAmount: tokensToSell,
    protocolFeeBps: PROTOCOL_FEE_BPS,
    creatorFeeBps: CREATOR_FEE_BPS,
  });
  const walletSol = lamportsToSol(exec.walletSolLamports);
  const curveSol = lamportsToSol(exec.curveSolLamports);
  const tokensSold = rawToTokenUi(exec.tokenRaw);
  ctx.state = exec.next;
  ledger.cashSol += walletSol;
  ledger.tokens = Math.max(0, ledger.tokens - tokensSold);
  ledger.realizedSol += walletSol;
  ledger.sells += 1;
  ctx.totalOut += walletSol;
  recordPrint(ctx, ts, "sell", walletSol, curveSol, tokensSold, label);
  return { walletSol, tokensSold };
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 4)}…` : id;
}

/**
 * Deterministically simulate a whole choreography. Pure: same input -> same
 * output. Re-run on every edit to drive the live chart + ledgers.
 */
export function simulate(choreo: Choreography): SimResult {
  const ctx: Ctx = {
    state: createInitialPumpState(),
    ledgers: new Map(choreo.wallets.map((w) => [w.id, emptyLedger(w)])),
    prints: [],
    markers: [],
    errors: [],
    solUsd: choreo.solUsd > 0 ? choreo.solUsd : 0,
    totalIn: 0,
    totalOut: 0,
  };

  // Dev buy lives in a synthetic "creator" ledger so it has somewhere to land.
  if (choreo.devBuySol > 0 && !ctx.ledgers.has("creator")) {
    ctx.ledgers.set("creator", { walletId: "creator", cashSol: choreo.devBuySol, tokens: 0, boughtTokens: 0, costBasisSol: 0, realizedSol: 0, buys: 0, sells: 0 });
  }

  // ---- LIVE TAPE → candles ----
  // The tape is the ordered list of trades with ABSOLUTE timestamps computed from
  // the playground (buildTape). Walking it records a print at each trade's real
  // time, so the candles strictly reflect the flow's timeline (buys spread out,
  // sells at their configured delay). Tape ⇐ playground; candles ⇐ tape.
  for (const a of choreo.tape ?? []) {
    if (a.kind === "buy") doBuy(ctx, a.walletId, a.sol ?? 0, a.t, a.label);
    else if (a.kind === "sell") doSell(ctx, a.walletId, a.pct ?? 0, a.t, a.label);
    else if (a.kind === "rotate") {
      const sold = doSell(ctx, a.walletId, a.sellPct ?? 100, a.t, a.label);
      if (sold && sold.walletSol > 0) {
        const to = ctx.ledgers.get(a.toWalletId ?? "");
        if (to) { to.cashSol += sold.walletSol; doBuy(ctx, a.toWalletId!, sold.walletSol, a.t, a.toTrader ?? a.label); }
      }
    } else if (a.kind === "airdrop") {
      const ledger = ctx.ledgers.get(a.walletId);
      if (ledger) {
        const tokensToDrop = ledger.tokens * (Math.max(0, Math.min(100, a.sellPct ?? 100)) / 100);
        if (tokensToDrop > 0) {
          ledger.tokens = Math.max(0, ledger.tokens - tokensToDrop);
          ctx.markers.push({ id: uid("mk"), time: a.t, side: "sell", label: `${a.walletId} airdrops`, priceSol: marketCapSolFromState(ctx.state) });
        }
      }
    }
  }

  const candles = candlesFromMarketCapPrints(ctx.prints, choreo.bucketMs > 0 ? choreo.bucketMs : DEFAULT_GAP_MS);
  const mcSol = marketCapSolFromState(ctx.state);
  return {
    prints: ctx.prints,
    candles,
    markers: ctx.markers,
    ledgers: [...ctx.ledgers.values()],
    finalState: ctx.state,
    marketCapSol: mcSol,
    marketCapUsd: mcSol * ctx.solUsd,
    bondingProgress: Math.min(100, pumpBondingProgressFromState(ctx.state) * 100), // 0..1 -> 0..100%
    totalSolIn: ctx.totalIn,
    totalSolOut: ctx.totalOut,
    errors: ctx.errors,
  };
}

